import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  Box,
  Button,
  VStack,
  Input,
  HStack,
  IconButton,
  Spinner,
  Text,
  Grid,
  GridItem,
  Heading,

} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react/field";
import { Dialog } from "@chakra-ui/react/dialog";
import {
  MdArrowBack as BackIcon,
  MdRestaurant as ServesIcon,
  MdTimer as PrepIcon,
  MdLocalFireDepartment as CookIcon,
} from "react-icons/md";
import { useLocation, useRoute } from "wouter";
import { RecipeService, useRecipeById, useTagGroups } from "@/lib/recipeService";
import { toaster } from "@/lib/toaster";
import type { CreateRecipeRequest, UpdateRecipeRequest } from "@/types/api-models";
import IngredientList from "@/pages/recipe-edit/IngredientList";
import MethodList from "@/pages/recipe-edit/MethodList";
import TagSheet from "@/pages/recipe-edit/TagSheet";

function FieldLabel({ htmlFor, icon, children }: { htmlFor?: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        fontWeight: 600,
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "var(--chakra-colors-text-muted)",
        marginBottom: "6px",
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      {icon}
      {children}
    </label>
  );
}

export default function RecipeEdit() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/recipes/:id/edit");
  const [newMatch] = useRoute("/recipes/new");
  const recipeId = params?.id || null;
  const isNewRecipe = !!newMatch;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [formData, setFormData] = useState({
    title: "",
    ingredients: [{ name: "", unit: "", quantity: "" }],
    instructions: [""],
    servings: "",
    prepTime: "",
    cookTime: "",
    tags: [] as string[],
  });

  const [errors, setErrors] = useState({ title: "", ingredients: "", instructions: "" });
  const titleRef = useRef<HTMLDivElement>(null);
  const ingredientsRef = useRef<HTMLDivElement>(null);
  const instructionsRef = useRef<HTMLDivElement>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState<typeof formData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showTagSheet, setShowTagSheet] = useState(false);
  // Pending selection while the sheet is open — committed on Apply
  const [pendingTags, setPendingTags] = useState<string[]>([]);

  const { recipe, isLoading } = useRecipeById(isNewRecipe ? undefined : recipeId ?? undefined);

  const { allTagGroups: tagGroups } = useTagGroups();

  const allGroupedTags = useMemo(
    () => (tagGroups || []).map(g => ({ name: g.name, tags: g.tags || [] })),
    [tagGroups],
  );

  useEffect(() => {
    if (recipe) {
      const initialData = {
        title: recipe.title || "",
        ingredients: recipe.ingredients?.map(ing => ({
          name: ing.name,
          unit: ing.unit || "",
          quantity: ing.quantity || "",
        })) || [{ name: "", unit: "", quantity: "" }],
        instructions: recipe.instructions || [""],
        servings: recipe.servings?.toString() || "",
        prepTime: recipe.prepTime || "",
        cookTime: recipe.cookTime || "",
        tags: recipe.tags || [],
      };
      setFormData(initialData);
      setOriginalData(initialData);
    } else if (isNewRecipe) {
      setOriginalData({
        title: "",
        ingredients: [{ name: "", unit: "", quantity: "" }],
        instructions: [""],
        servings: "",
        prepTime: "",
        cookTime: "",
        tags: [],
      });
    }
  }, [recipe, isNewRecipe]);

  useEffect(() => {
    if (!originalData) return;
    setHasUnsavedChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
  }, [formData, originalData]);

  const doSave = async (data: CreateRecipeRequest | UpdateRecipeRequest) => {
    setIsSaving(true);
    try {
      let saved;
      if (isNewRecipe) {
        saved = await RecipeService.create(data as CreateRecipeRequest);
      } else {
        saved = await RecipeService.update(recipeId!, data as UpdateRecipeRequest);
      }
      navigate(`/recipes/${saved.id}`);
    } catch (err) {
      toaster.create({
        title: "Failed to save recipe",
        description: err instanceof Error ? err.message : "Something went wrong",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const buildPayload = (): CreateRecipeRequest | UpdateRecipeRequest => ({
    title: formData.title.trim(),
    ingredients: formData.ingredients
      .filter(ing => ing.name.trim())
      .map(ing => ({ ...ing, quantity: ing.quantity.trim() || null, unit: ing.unit.trim() || null })),
    instructions: formData.instructions.filter(inst => inst.trim()),
    prepTime: formData.prepTime,
    cookTime: formData.cookTime,
    tags: formData.tags,
    ...(formData.servings.trim() ? { servings: formData.servings.trim() } : {}),
  });

  const handleSubmit = () => {
    const newErrors = { title: "", ingredients: "", instructions: "" };
    if (!formData.title.trim()) newErrors.title = "Recipe title is required";
    if (!formData.ingredients.some(ing => ing.name.trim())) newErrors.ingredients = "At least one ingredient is required";
    if (!formData.instructions.some(inst => inst.trim())) newErrors.instructions = "At least one instruction is required";
    const hasErrors = Object.values(newErrors).some(Boolean);
    setErrors(newErrors);
    if (hasErrors) {
      setTimeout(() => {
        const ref = newErrors.title ? titleRef : newErrors.ingredients ? ingredientsRef : instructionsRef;
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    void doSave(buildPayload());
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleIngredientChange = (index: number, field: string, value: string) => {
    const next = [...formData.ingredients];
    next[index] = { ...next[index]!, [field]: value };
    setFormData(prev => ({ ...prev, ingredients: next }));
    if (errors.ingredients) setErrors(prev => ({ ...prev, ingredients: "" }));
  };

  const addIngredient = () => {
    setFormData(prev => ({ ...prev, ingredients: [...prev.ingredients, { name: "", unit: "", quantity: "" }] }));
  };

  const removeIngredient = (index: number) => {
    if (formData.ingredients.length > 1)
      setFormData(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== index) }));
  };

  const handleInstructionChange = (index: number, value: string) => {
    const next = [...formData.instructions];
    next[index] = value;
    setFormData(prev => ({ ...prev, instructions: next }));
    if (errors.instructions) setErrors(prev => ({ ...prev, instructions: "" }));
  };

  const addInstruction = () => {
    setFormData(prev => ({ ...prev, instructions: [...prev.instructions, ""] }));
  };

  const removeInstruction = (index: number) => {
    if (formData.instructions.length > 1)
      setFormData(prev => ({ ...prev, instructions: prev.instructions.filter((_, i) => i !== index) }));
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) setShowUnsavedDialog(true);
    else window.history.back();
  };

  const openTagSheet = () => {
    setPendingTags([...formData.tags]);
    setShowTagSheet(true);
  };

  const applyTags = () => {
    setFormData(prev => ({ ...prev, tags: pendingTags }));
    setShowTagSheet(false);
  };

  const togglePendingTag = (tag: string) => {
    setPendingTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  if (isLoading) {
    return (
      <Box minH="100vh" bg="bg.canvas" display="flex" alignItems="center" justifyContent="center">
        <VStack gap={4}>
          <Spinner size="xl" color="accent.emphasis" />
          <Text color="text.muted">Loading recipe…</Text>
        </VStack>
      </Box>
    );
  }

  const sectionHeadingStyle = {
    fontFamily: "heading",
    fontWeight: "800",
    fontSize: "12px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.14em",
    pb: "6px",
    borderBottom: "2px solid",
    borderColor: "accent.emphasis",
    display: "inline-block",
    mb: "12px",
    color: "text.primary" as const,
  };

  const inputStyle = {
    bg: "bg.surface",
    borderColor: "border.muted",
    borderRadius: "10px",
    _hover: { borderColor: "accent.secondary" },
    _focus: { borderColor: "accent.emphasis", boxShadow: "0 0 0 3px var(--chakra-colors-accent-subtle)" },
    color: "text.primary",
    fontSize: "15px",
    fontWeight: "500",
  };

  const smallInputStyle = {
    bg: "bg.surface",
    border: "1.5px solid",
    borderColor: "border.default",
    borderRadius: "8px",
    _hover: { borderColor: "accent.secondary" },
    _focus: { borderColor: "accent.emphasis", boxShadow: "none" },
    color: "text.primary",
    fontSize: { base: "13px", lg: "14px" },
    size: { base: "sm", lg: "md" } as const,
    w: "100%",
  };

  const dashedAddStyle = {
    variant: "outline" as const,
    borderStyle: "dashed",
    borderColor: "border.muted",
    color: "accent.secondary",
    bg: "transparent",
    borderRadius: "10px",
    fontWeight: "600",
    fontSize: "14px",
    py: "13px",
    w: "100%",
    _hover: { borderColor: "accent.secondary", bg: "accent.secondarySubtle" },
    gap: 2,
  };

  return (
    <Box minH="100vh" bg="bg.canvas">

      {/* Safe area spacer */}
      <Box h="env(safe-area-inset-top, 0px)" />

      {/* Form */}
      <Box maxW={{ base: "container.sm", lg: "760px" }} px={{ base: 4, lg: 8 }} pt={{ base: 5, lg: 8 }} pb="120px">

        {/* Page header — matches Household page style */}
        <Box mb={6} display="flex" alignItems="flex-start" gap={3}>
          <IconButton
            aria-label="Go back"
            variant="ghost"
            bg="bg.muted"
            borderRadius="10px"
            size="sm"
            color="accent.secondary"
            _hover={{ bg: "bg.subtle" }}
            onClick={handleBackClick}
            flexShrink={0}
            mt="3px"
            display={{ base: "flex", lg: "none" }}
          >
            <BackIcon />
          </IconButton>
          <Box>
            {!isNewRecipe && (
              <Text fontFamily="body" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.12em" color="accent.secondary" mb="2px">
                Editing
              </Text>
            )}
            <Heading
              fontFamily="heading"
              fontWeight="800"
              fontSize={{ base: "xl", md: "2xl" }}
              color="text.primary"
              lineHeight="1.1"
            >
              {isNewRecipe ? "New recipe" : (formData.title || "Edit recipe")}
            </Heading>
          </Box>
        </Box>
        <VStack gap={7} align="stretch">

          {/* Title */}
          <Box ref={titleRef} w="100%"><Field.Root invalid={!!errors.title} w="100%">
            <FieldLabel htmlFor="recipe-title">Title</FieldLabel>
            <Input
              id="recipe-title"
              value={formData.title}
              onChange={e => handleInputChange("title", e.target.value)}
              placeholder="What are we making?"
              {...inputStyle}
              padding="12px 14px"
            />
            {errors.title && <Field.ErrorText>{errors.title}</Field.ErrorText>}
          </Field.Root></Box>

          {/* Details */}
          <Grid templateColumns="1fr 1fr 1fr" gap={3}>
            <GridItem>
              <FieldLabel htmlFor="recipe-servings" icon={<ServesIcon color="var(--chakra-colors-accent-secondary)" />}>Serves</FieldLabel>
              <Input id="recipe-servings" value={formData.servings} onChange={e => handleInputChange("servings", e.target.value)} placeholder="4" {...inputStyle} padding="11px 10px" />
            </GridItem>
            <GridItem>
              <FieldLabel htmlFor="recipe-prep-time" icon={<PrepIcon color="var(--chakra-colors-accent-secondary)" />}>Prep</FieldLabel>
              <Input id="recipe-prep-time" value={formData.prepTime} onChange={e => handleInputChange("prepTime", e.target.value)} placeholder="10 min" {...inputStyle} padding="11px 10px" />
            </GridItem>
            <GridItem>
              <FieldLabel htmlFor="recipe-cook-time" icon={<CookIcon color="var(--chakra-colors-accent-emphasis)" />}>Cook</FieldLabel>
              <Input id="recipe-cook-time" value={formData.cookTime} onChange={e => handleInputChange("cookTime", e.target.value)} placeholder="30 min" {...inputStyle} padding="11px 10px" />
            </GridItem>
          </Grid>

          {/* Tags */}
          <Box>
            <Box {...sectionHeadingStyle}>Tags</Box>
            <Box
              as="button"
              id="recipe-tags"
              w="100%"
              textAlign="left"
              bg="bg.surface"
              border="1px solid"
              borderColor="border.muted"
              borderRadius="10px"
              px={3}
              py="9px"
              minH="44px"
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              cursor="pointer"
              _hover={{ borderColor: "accent.secondary" }}
              onClick={openTagSheet}
              style={{ width: "100%", textAlign: "left" }}
            >
              {formData.tags.length === 0 ? (
                <Text fontSize="14px" color="text.muted" fontWeight="400">Add tags…</Text>
              ) : (
                formData.tags.map(tag => (
                  <Box key={tag} bg="accent.secondarySubtle" color="accent.secondary" fontSize="12px" fontWeight="600" borderRadius="full" px="10px" py="3px">
                    {tag}
                  </Box>
                ))
              )}
              <Box ml="auto" color="text.muted" display="flex" alignItems="center" flexShrink={0}>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
            </Box>
          </Box>

          {/* Ingredients */}
          <IngredientList
            ingredients={formData.ingredients}
            error={errors.ingredients}
            onChange={handleIngredientChange}
            onAdd={addIngredient}
            onRemove={removeIngredient}
            sectionHeadingStyle={sectionHeadingStyle}
            smallInputStyle={smallInputStyle}
            dashedAddStyle={dashedAddStyle}
            fieldRef={ingredientsRef}
          />

          {/* Method */}
          <MethodList
            instructions={formData.instructions}
            error={errors.instructions}
            onChange={handleInstructionChange}
            onAdd={addInstruction}
            onRemove={removeInstruction}
            sectionHeadingStyle={sectionHeadingStyle}
            dashedAddStyle={dashedAddStyle}
            fieldRef={instructionsRef}
          />

        </VStack>
      </Box>

      {/* Sticky footer — 1c pattern */}
      <Box
        position="fixed"
        bottom={0}
        left={{ base: 0, lg: "260px" }}
        right={0}
        bg="bg.surface"
        borderTop="1px solid"
        borderColor="border.muted"
        pt={3}
        pb="calc(12px + env(safe-area-inset-bottom))"
        zIndex={10}
      >
        <Box maxW={{ base: "container.sm", lg: "760px" }} px={{ base: 4, lg: 8 }}>
          <HStack gap={3} justify={{ base: "stretch", lg: "flex-end" }}>
            <Button
              flex={{ base: 1, lg: "none" }}
              w={{ lg: "120px" }}
              variant="outline"
              borderColor="border.muted"
              color="text.primary"
              borderRadius="10px"
              fontWeight="600"
              _hover={{ bg: "bg.muted" }}
              onClick={handleBackClick}
            >
              Cancel
            </Button>
            <Button
              flex={{ base: 2, lg: "none" }}
              w={{ lg: "160px" }}
              bg="accent.emphasis"
              color="white"
              borderRadius="10px"
              fontFamily="heading"
              fontWeight="800"
              _hover={{ bg: "accent.emphasisHover" }}
              onClick={handleSubmit}
              loading={isSaving}
            >
              {isNewRecipe ? "Create recipe" : "Save recipe"}
            </Button>
          </HStack>
        </Box>
      </Box>

      {/* Tag sheet */}
      <TagSheet
        isOpen={showTagSheet}
        onClose={() => setShowTagSheet(false)}
        tagGroups={allGroupedTags}
        pendingTags={pendingTags}
        onToggle={togglePendingTag}
        onApply={applyTags}
      />

      {/* Unsaved changes dialog */}
      <Dialog.Root open={showUnsavedDialog} onOpenChange={d => setShowUnsavedDialog(d.open)}>
        <Dialog.Backdrop bg="blackAlpha.600" />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" border="1px solid" borderColor="border.muted" shadow="xl" borderRadius="xl" overflow="hidden" mx={{ base: 3, sm: 0 }}>
            <Dialog.Header borderBottomWidth="1px" borderColor="border.muted" fontWeight="600" color="text.primary">
              Unsaved changes
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6} px={{ base: 5, sm: 6 }} pt={5}>
              <Text>You have unsaved changes. Save them before leaving?</Text>
            </Dialog.Body>
            <Dialog.Footer gap={3} borderTopWidth="1px" borderColor="border.muted" bg="bg.muted" justifyContent="flex-end">
              <Button
                variant="outline"
                borderColor="border.muted"
                color="text.primary"
                _hover={{ bg: "bg.muted" }}
                onClick={() => {
                  setShowUnsavedDialog(false);
                  setHasUnsavedChanges(false);
                  window.history.back();
                }}
              >
                Discard
              </Button>
              <Button
                bg="accent.emphasis"
                color="white"
                _hover={{ bg: "accent.emphasisHover" }}
                loading={isSaving}
                onClick={() => {
                  setShowUnsavedDialog(false);
                  handleSubmit();
                }}
              >
                Save
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

    </Box>
  );
}
